import * as m from '@staiter/i18n';
import type { Integration, IntegrationHealth } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { History, Pencil, Plug2, Plus, ShieldCheck, ShieldOff, Trash2, Upload } from 'lucide-react';
import { useState } from 'react';
import { PageHeader, Table } from '~/components/data';
import { UserPicker } from '~/components/domain';
import { useToast } from '~/components/feedback';
import { Button, ConfirmDialog, Dialog, Drawer, Input, Select } from '~/components/primitives';
import {
  type AddIntegrationInput,
  createIntegration,
  deleteIntegration,
  fetchIntegrationHealth,
  fetchIntegrations,
  fetchOnecPushHistory,
  testIntegrationById,
  testIntegrationConnection,
  updateIntegration,
} from '~/lib/api/admin';
import { useAuthStore } from '~/store/auth';
import shared from './_auth._shared.module.css';
import styles from './_auth.admin.module.css';

export const Route = createFileRoute('/_auth/admin/integrations')({
  staticData: { minMainWidth: 960 },
  component: IntegrationsAdmin,
});

function healthBadgeLabel(health: IntegrationHealth): string {
  switch (health) {
    case 'ok':
      return m.route_admin_integrations_health_badge_ok();
    case 'degraded':
      return m.route_admin_integrations_health_badge_degraded();
    case 'disconnected':
      return m.route_admin_integrations_health_badge_disconnected();
  }
}

function HealthBadge({ health }: { health: IntegrationHealth }): JSX.Element {
  const cls =
    health === 'ok'
      ? styles.healthOk
      : health === 'degraded'
        ? styles.healthWarn
        : styles.healthDanger;
  return <span className={[styles.healthBadge, cls].join(' ')}>{healthBadgeLabel(health)}</span>;
}

function formatTs(ts: string | null): string {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

function maskedDisplay(value: string | null | undefined): string {
  if (!value) return '—';
  if (value.length <= 4) return `••••${value}`;
  return `••••${value.slice(-4)}`;
}

function IntegrationsAdmin(): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  // multi-tenancy-rls F1 — ops-видимость defense-in-depth слоя (RLS). Источник —
  // boolean `rls_active` из /auth/me (B2). Только admin (этот роут admin-only).
  const rlsActive = useAuthStore(
    (s) => (s.user as { rls_active?: boolean } | null)?.rls_active ?? false,
  );
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'integrations'],
    queryFn: fetchIntegrations,
  });
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  // FE-P3-3 follow-up: edit + delete dialogs per row.
  const [editTarget, setEditTarget] = useState<Integration | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Integration | null>(null);
  const [onecHistoryOpen, setOnecHistoryOpen] = useState(false);

  const onecHistory = useQuery({
    queryKey: ['admin', 'integrations', 'onec', 'history'],
    queryFn: () => fetchOnecPushHistory(),
    enabled: onecHistoryOpen,
  });

  const updateMut = useMutation({
    mutationFn: (input: { id: string; sync_mode: AddIntegrationInput['sync_mode'] }) =>
      updateIntegration(input.id, { sync_mode: input.sync_mode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'integrations'] }),
  });

  // FE-P3-3 follow-up: soft-delete (disable + scrub credentials).
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteIntegration(id),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.admin_integrations_disabled_toast() });
      void qc.invalidateQueries({ queryKey: ['admin', 'integrations'] });
      setDeleteTarget(null);
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.admin_integrations_disable_failed(),
        description: err.message,
      }),
  });

  // FE-P3-3 follow-up: rotate credentials через existing PUT endpoint.
  const editMut = useMutation({
    mutationFn: (input: {
      id: string;
      peer: '1c' | 'bitrix' | 'adesk';
      credentials?: Record<string, string>;
      is_enabled?: boolean;
      sync_mode: AddIntegrationInput['sync_mode'];
      approver_user_id?: string;
      reason?: string;
      scope?: AddIntegrationInput['scope'];
    }) =>
      updateIntegration(input.id, {
        peer: input.peer,
        sync_mode: input.sync_mode,
        ...(input.credentials ? { credentials: input.credentials } : {}),
        ...(input.is_enabled !== undefined ? { is_enabled: input.is_enabled } : {}),
        ...(input.approver_user_id ? { approver_user_id: input.approver_user_id } : {}),
        ...(input.reason ? { reason: input.reason } : {}),
        ...(input.scope ? { scope: input.scope } : {}),
      }),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.admin_integrations_saved() });
      void qc.invalidateQueries({ queryKey: ['admin', 'integrations'] });
      setEditTarget(null);
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.admin_integrations_save_failed(),
        description: err.message,
      }),
  });

  // FE-P3-3 follow-up (pass3 closure): test connection per row.
  const testMut = useMutation({
    mutationFn: (id: string) => testIntegrationById(id),
    onSuccess: (res) => {
      toast.push({
        variant: res.ok ? 'success' : 'error',
        title: res.ok ? m.admin_integrations_conn_ok() : m.admin_integrations_conn_error(),
        description:
          res.message ??
          (res.credentials_ok
            ? m.admin_integrations_creds_valid()
            : m.admin_integrations_creds_invalid()),
      });
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.admin_integrations_conn_error(),
        description: err.message,
      }),
  });

  const items = (data?.items ?? []) as Integration[];

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.route_admin_integrations_title()}
        description={m.route_admin_integrations_description()}
        actions={
          <>
            <Button
              size="sm"
              variant="secondary"
              leadingIcon={<Plug2 size={14} />}
              onClick={() => navigate({ to: '/admin/integrations/platform' })}
            >
              {m.integ_platform_link()}
            </Button>
            <Button size="sm" leadingIcon={<Plus size={14} />} onClick={() => setWizardOpen(true)}>
              {m.route_admin_integrations_btn_add()}
            </Button>
          </>
        }
      />

      {/* multi-tenancy-rls F1 — RLS-mode индикатор (admin ops-видимость).
          Статус = StatusBadge-пилюля (icon + цвет + текст, §6.1 visual grammar):
          ON = ok/✓, OFF = degraded/warn (штатное, но защита-OFF). a11y: role=status. */}
      <div
        role="status"
        aria-label={`${m.route_admin_integrations_rls_label()}: ${
          rlsActive ? m.route_admin_integrations_rls_on() : m.route_admin_integrations_rls_off()
        }`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2, 6px)',
          padding: '10px 14px',
          marginBottom: 12,
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          background: 'var(--bg-surface)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>
            {m.route_admin_integrations_rls_label()}
          </span>
          <span
            className={[styles.healthBadge, rlsActive ? styles.healthOk : styles.healthWarn].join(
              ' ',
            )}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            {rlsActive ? (
              <ShieldCheck size={13} aria-hidden="true" />
            ) : (
              <ShieldOff size={13} aria-hidden="true" />
            )}
            {rlsActive ? m.route_admin_integrations_rls_on() : m.route_admin_integrations_rls_off()}
          </span>
        </div>
        <span className={styles.muted} style={{ fontSize: 12, maxWidth: '72ch' }}>
          {m.route_admin_integrations_rls_hint()}
        </span>
      </div>

      <Table
        ariaLabel={m.route_admin_integrations_title()}
        loading={isLoading}
        data={items}
        rowKey={(i) => i.id}
        columns={[
          {
            id: 'peer',
            header: m.route_admin_integrations_col_peer(),
            accessor: (i) => (
              <span>
                <Plug2 size={12} aria-hidden="true" /> {i.peer.toUpperCase()} — {i.name}
              </span>
            ),
          },
          {
            id: 'health',
            header: m.route_admin_integrations_col_health(),
            accessor: (i) => <HealthBadge health={i.health} />,
          },
          {
            id: 'sync_mode',
            header: m.route_admin_integrations_col_sync_mode(),
            accessor: (i) => (
              <Select
                label=""
                value={i.sync_mode}
                onChange={(e) =>
                  updateMut.mutate({
                    id: i.id,
                    sync_mode: e.target.value as AddIntegrationInput['sync_mode'],
                  })
                }
                options={[
                  { value: 'pull', label: m.route_admin_integrations_sync_pull() },
                  { value: 'push', label: m.route_admin_integrations_sync_push() },
                  {
                    value: 'bidirectional',
                    label: m.route_admin_integrations_sync_bidirectional(),
                  },
                  { value: 'disabled', label: m.route_admin_integrations_sync_disabled() },
                ]}
                size="sm"
              />
            ),
          },
          {
            id: 'last_pull',
            header: m.route_admin_integrations_col_last_pull(),
            accessor: (i) => (
              <span className={styles.mono}>{formatTs(i.last_pull_at ?? null)}</span>
            ),
          },
          {
            id: 'last_push',
            header: m.route_admin_integrations_col_last_push(),
            accessor: (i) => (
              <span className={styles.mono}>{formatTs(i.last_push_at ?? null)}</span>
            ),
          },
          {
            id: 'creds',
            header: m.route_admin_integrations_col_credentials(),
            accessor: (i) => (
              <span className={styles.muted}>
                {!i.credentials || i.credentials.length === 0
                  ? '—'
                  : i.credentials
                      .map((c) => `${c.label}: ${maskedDisplay(c.masked_value)}`)
                      .join(', ')}
              </span>
            ),
          },
          {
            id: 'actions',
            header: '',
            accessor: (i) => (
              <div className={styles.actionsCell}>
                {/* FE-P3-3 follow-up (pass3 closure): edit credentials + sync_mode + is_enabled. */}
                <Button
                  size="sm"
                  variant="secondary"
                  leadingIcon={<Pencil size={12} />}
                  onClick={() => setEditTarget(i)}
                  title={m.ui_izmenit_nastroyki_integracii()}
                >
                  {m.ui_izmenit()}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  leadingIcon={<Plug2 size={12} />}
                  onClick={() => testMut.mutate(i.id)}
                  disabled={testMut.isPending}
                  title={m.ui_proverit_podklyuchenie_k_peer()}
                >
                  {m.ui_proverit()}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  leadingIcon={<History size={12} />}
                  onClick={() => setHistoryId(i.id)}
                >
                  {m.route_admin_integrations_btn_health_history()}
                </Button>
                {i.peer === '1c' && (
                  <>
                    {/* F91 (Ф6): отправки в 1С — заявкой уровня 3 на экране «Финансы»,
                        бухгалтер в админку не попадает (К3-7). */}
                    <Link to="/finance/onec-push" className={styles.movedLink}>
                      <Upload size={12} aria-hidden="true" /> {m.admin_onec_push_moved_link()}
                    </Link>
                    <Button
                      size="sm"
                      variant="ghost"
                      leadingIcon={<History size={12} />}
                      onClick={() => setOnecHistoryOpen(true)}
                    >
                      {m.route_admin_integrations_btn_push_history()}
                    </Button>
                  </>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  leadingIcon={<Trash2 size={12} />}
                  onClick={() => setDeleteTarget(i)}
                  title={m.admin_integrations_disable_and_wipe()}
                >
                  {m.ui_udalit()}
                </Button>
              </div>
            ),
          },
        ]}
      />

      <HealthHistoryDrawer integrationId={historyId} onClose={() => setHistoryId(null)} />

      {/* FE-P3-3 follow-up: edit dialog (creds rotate + is_enabled + sync_mode). */}
      {editTarget && (
        <EditIntegrationDialog
          integration={editTarget}
          submitting={editMut.isPending}
          onClose={() => setEditTarget(null)}
          onSave={(input) =>
            editMut.mutate({
              id: editTarget.id,
              peer: editTarget.peer,
              ...input,
            })
          }
        />
      )}

      {/* FE-P3-3 follow-up: confirm delete (soft-delete). */}
      {deleteTarget && (
        <ConfirmDialog
          open
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => deleteMut.mutate(deleteTarget.id)}
          title={m.admin_integrations_disable_title()}
          description={`Интеграция ${deleteTarget.peer.toUpperCase()} будет отключена и учётные данные удалены. Это необратимо без повторной настройки.`}
          confirmLabel={m.me_2fa_disable()}
          cancelLabel={m.ui_otmena()}
          variant="danger"
          loading={deleteMut.isPending}
        />
      )}

      <AddIntegrationWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={() => {
          setWizardOpen(false);
          void qc.invalidateQueries({ queryKey: ['admin', 'integrations'] });
        }}
      />

      {/* 1С push history drawer */}
      <Drawer
        open={onecHistoryOpen}
        onClose={() => setOnecHistoryOpen(false)}
        title={m.route_admin_integrations_push_history_title()}
        width={640}
      >
        {onecHistory.isLoading && <p className={styles.muted}>{m.common_loader_loading()}</p>}
        {!onecHistory.isLoading && (onecHistory.data?.items.length ?? 0) === 0 && (
          <p className={styles.muted}>{m.route_admin_integrations_push_history_empty()}</p>
        )}
        <div className={styles.historyList}>
          {(onecHistory.data?.items ?? []).map((ev) => (
            <div key={ev.id} className={styles.historyItem}>
              <div className={styles.historyHeader}>
                <span className={styles.historyKind}>
                  {ev.kind} · {ev.status}
                </span>
                <span className={styles.historyTs}>{formatTs(ev.pushed_at)}</span>
              </div>
              <p className={styles.historyPayload}>{ev.payload_summary}</p>
              <p className={styles.historyMeta}>
                {m.route_admin_integrations_history_meta({
                  actor: ev.pushed_by,
                  count: (ev.approver_signatures ?? []).length,
                })}
                {ev.period &&
                  ` · ${m.route_admin_integrations_history_period({ period: ev.period })}`}
              </p>
              {ev.error && <p className={styles.historyError}>{ev.error}</p>}
              {ev.rolled_back_at && (
                <p className={styles.historyRollback}>
                  {m.route_admin_integrations_history_rolled_back({
                    ts: formatTs(ev.rolled_back_at),
                  })}
                </p>
              )}
            </div>
          ))}
        </div>
      </Drawer>
    </div>
  );
}

function HealthHistoryDrawer({
  integrationId,
  onClose,
}: {
  integrationId: string | null;
  onClose: () => void;
}): JSX.Element {
  const enabled = !!integrationId;
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'integrations', integrationId, 'health'],
    queryFn: () => fetchIntegrationHealth(integrationId!),
    enabled,
  });
  return (
    <Drawer
      open={enabled}
      onClose={onClose}
      title={m.route_admin_integrations_health_history_title()}
      width={520}
    >
      {isLoading && <p className={styles.muted}>{m.common_loader_loading()}</p>}
      {!isLoading && (data?.items.length ?? 0) === 0 && (
        <p className={styles.muted}>{m.route_admin_integrations_health_history_empty()}</p>
      )}
      <div className={styles.timeline}>
        {(data?.items ?? []).map((ev) => (
          <div key={ev.id} className={styles.timelineItem}>
            <span className={styles.mono}>{formatTs(ev.occurred_at)}</span>
            <HealthBadge health={ev.health} />
            <span>{ev.message ?? '—'}</span>
          </div>
        ))}
      </div>
    </Drawer>
  );
}

function AddIntegrationWizard({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [step, setStep] = useState<'select' | 'creds' | 'test' | 'save'>('select');
  const [peer, setPeer] = useState<'1c' | 'bitrix' | 'adesk'>('1c');
  const [name, setName] = useState('');
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<{ ok: boolean; message?: string } | null>(null);
  const [revealed, setRevealed] = useState<string | null>(null);

  const reset = (): void => {
    setStep('select');
    setPeer('1c');
    setName('');
    setCreds({});
    setTestResult(null);
    setRevealed(null);
  };

  const close = (): void => {
    reset();
    onClose();
  };

  const credFields = CREDENTIAL_FIELDS[peer];

  const testMut = useMutation({
    mutationFn: () =>
      testIntegrationConnection({
        peer,
        name,
        sync_mode: 'pull',
        credentials: creds,
      }),
    onSuccess: (r) => {
      setTestResult(r);
      if (r.ok) setStep('save');
    },
    onError: (err: Error) => setTestResult({ ok: false, message: err.message }),
  });

  const saveMut = useMutation({
    mutationFn: () =>
      createIntegration({
        peer,
        name,
        sync_mode: 'pull',
        credentials: creds,
      }),
    onSuccess: (created) => {
      // S8.1: показываем raw credentials один раз для копирования.
      const lastCred = Object.values(creds).find((v) => v.length > 0) ?? null;
      setRevealed(lastCred);
      setTimeout(() => {
        onCreated();
        reset();
      }, 6_000);
    },
  });

  return (
    <Dialog
      open={open}
      onClose={close}
      title={m.route_admin_integrations_wizard_title()}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {m.common_button_cancel()}
          </Button>
          {step === 'select' && (
            <Button onClick={() => setStep('creds')} disabled={!name.trim()}>
              {m.common_button_next()}
            </Button>
          )}
          {step === 'creds' && (
            <Button
              onClick={() => {
                setStep('test');
                testMut.mutate();
              }}
              disabled={credFields.some((f) => f.required && !creds[f.key])}
            >
              {m.route_admin_integrations_wizard_test_conn()}
            </Button>
          )}
          {step === 'test' && (
            <Button onClick={() => setStep('creds')} variant="secondary">
              {m.common_button_back()}
            </Button>
          )}
          {step === 'save' && (
            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              {saveMut.isPending ? m.common_loader_saving() : m.common_button_save()}
            </Button>
          )}
        </>
      }
    >
      {step === 'select' && (
        <div>
          <div className={styles.formRow}>
            <Select
              label={m.route_admin_integrations_wizard_source_type()}
              value={peer}
              onChange={(e) => setPeer(e.target.value as typeof peer)}
              options={[
                { value: '1c', label: m.route_admin_integrations_peer_1c_label() },
                { value: 'bitrix', label: m.route_admin_integrations_peer_bitrix_label() },
                { value: 'adesk', label: m.route_admin_integrations_peer_adesk_label() },
              ]}
            />
          </div>
          <div className={styles.formRow}>
            <Input
              label={m.route_admin_integrations_wizard_name()}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={m.route_admin_integrations_wizard_name_placeholder()}
            />
          </div>
        </div>
      )}
      {step === 'creds' && (
        <div>
          <p className={styles.muted}>{m.route_admin_integrations_wizard_creds_hint()}</p>
          {credFields.map((f) => (
            <div key={f.key} className={styles.formRow}>
              <Input
                label={`${f.label}${f.required ? ' *' : ''}`}
                type={f.type === 'password' ? 'password' : 'text'}
                value={creds[f.key] ?? ''}
                onChange={(e) => setCreds((s) => ({ ...s, [f.key]: e.target.value }))}
                autoComplete="off"
              />
            </div>
          ))}
        </div>
      )}
      {step === 'test' && (
        <div>
          {testMut.isPending && <p>{m.route_admin_integrations_wizard_testing()}</p>}
          {testResult && (
            <div
              className={[
                styles.warning,
                testResult.ok ? styles.healthOk : styles.healthDanger,
              ].join(' ')}
            >
              {testResult.ok
                ? m.route_admin_integrations_wizard_test_ok()
                : m.route_admin_integrations_wizard_test_error({
                    message: testResult.message ?? 'unknown',
                  })}
            </div>
          )}
        </div>
      )}
      {step === 'save' && (
        <div>
          {revealed === null && <p>{m.route_admin_integrations_wizard_ready_to_save()}</p>}
          {revealed && (
            <>
              <p className={styles.muted}>{m.route_admin_integrations_wizard_reveal_hint()}</p>
              <pre className={styles.payloadJson}>{revealed}</pre>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}

const CREDENTIAL_FIELDS: Record<
  '1c' | 'bitrix' | 'adesk',
  Array<{ key: string; label: string; type: 'text' | 'password' | 'url'; required: boolean }>
> = {
  '1c': [
    { key: 'base_url', label: 'OData base URL', type: 'url', required: true },
    { key: 'username', label: 'Username', type: 'text', required: true },
    { key: 'password', label: 'Password', type: 'password', required: true },
  ],
  bitrix: [{ key: 'webhook_url', label: 'Inbound webhook URL', type: 'url', required: true }],
  adesk: [{ key: 'api_key', label: 'API key', type: 'password', required: true }],
};

// FE-P3-3 follow-up (pass3 closure): edit existing integration dialog.
// Позволяет admin'у: change credentials (rotate), toggle is_enabled (pause),
// change sync_mode. Использует существующий PUT endpoint.
function EditIntegrationDialog({
  integration,
  submitting,
  onClose,
  onSave,
}: {
  integration: Integration;
  submitting: boolean;
  onClose: () => void;
  onSave: (input: {
    credentials?: Record<string, string>;
    is_enabled?: boolean;
    sync_mode: AddIntegrationInput['sync_mode'];
    approver_user_id?: string;
    reason?: string;
    scope?: AddIntegrationInput['scope'];
  }) => void;
}): JSX.Element {
  const credFields = CREDENTIAL_FIELDS[integration.peer];
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [isEnabled, setIsEnabled] = useState<boolean>(integration.is_enabled ?? true);
  const initialSyncMode = integration.sync_mode as AddIntegrationInput['sync_mode'];
  const [syncMode, setSyncMode] = useState<AddIntegrationInput['sync_mode']>(initialSyncMode);
  // BE-P3-1 (pass3 backend): когда sync_mode меняется — нужен Tier 2 named
  // approver (≠ requester) + reason. UI показывает approver picker inline.
  const [approverUserId, setApproverUserId] = useState('');
  const [reason, setReason] = useState('');
  const syncModeChanging = syncMode !== initialSyncMode;
  // INT-DRAWER-2: per-entity scope toggles. Local state до Save.
  const initialScope = {
    items: integration.scope?.items ?? true,
    warehouses: integration.scope?.warehouses ?? true,
    boms: integration.scope?.boms ?? true,
    counterparties: integration.scope?.counterparties ?? true,
    batches: integration.scope?.batches ?? true,
  };
  const [scope, setScope] = useState(initialScope);
  const scopeChanging = (Object.keys(initialScope) as Array<keyof typeof initialScope>).some(
    (k) => initialScope[k] !== scope[k],
  );
  const noneSelected = !Object.values(scope).some(Boolean);

  const hasNewCreds = Object.values(creds).some((v) => v.length > 0);
  const canSave =
    !submitting && (!syncModeChanging || (approverUserId.length > 0 && reason.trim().length > 0));

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Изменить ${integration.peer.toUpperCase()}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {m.ui_otmena()}
          </Button>
          <Button
            variant="primary"
            disabled={!canSave}
            onClick={() =>
              onSave({
                sync_mode: syncMode,
                is_enabled: isEnabled,
                ...(hasNewCreds ? { credentials: creds } : {}),
                ...(syncModeChanging
                  ? { approver_user_id: approverUserId, reason: reason.trim() }
                  : {}),
                ...(scopeChanging ? { scope } : {}),
              })
            }
          >
            {m.ui_sohranit()}
          </Button>
        </>
      }
    >
      <div className={styles.editGroup}>
        <Select
          label={m.admin_integrations_sync_mode()}
          value={syncMode}
          onChange={(e) => setSyncMode(e.target.value as AddIntegrationInput['sync_mode'])}
          options={[
            { value: 'pull', label: m.admin_integrations_mode_pull() },
            { value: 'push', label: m.admin_integrations_mode_push() },
            { value: 'bidirectional', label: m.admin_integrations_mode_both() },
            { value: 'disabled', label: m.admin_integrations_state_disabled() },
          ]}
        />
        {syncModeChanging && (
          <div
            style={{
              padding: 12,
              border: '1px solid var(--color-warn, var(--color-border))',
              borderRadius: 6,
              background: 'color-mix(in oklch, var(--warn, var(--bg-surface)), transparent 92%)',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <strong style={{ fontSize: 13 }}>
              {m.ui_izmenenie_rezhima_sinhronizacii_tier_2_operaciya()}
            </strong>
            <p className={styles.muted} style={{ margin: 0, fontSize: 12 }}>
              Требуется именованный одобряющий (отличный от вас, роль ≥ manager) и причина для audit
              trail.
            </p>
            {/* biome-ignore lint/a11y/useValidAriaRole: UserPicker.role — фильтр по user role, не ARIA */}
            <UserPicker
              label={m.admin_integrations_approver()}
              value={approverUserId}
              onChange={setApproverUserId}
              role="manager"
              placeholder={m.admin_integrations_pick_approver()}
            />
            <Input
              label={m.admin_integrations_reason()}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={m.admin_integrations_reason_example()}
            />
          </div>
        )}
        <div className={styles.editToggleRow}>
          <label>
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
            />
            <span>{m.ui_nbsp_vklyuchena()}</span>
          </label>
          <span className={styles.muted}>
            {m.ui_otklyuchennye_integracii_ne_sinhroniziruyutsya_no_ne_tery()}
          </span>
        </div>
        {/* INT-DRAWER-2: per-entity scope toggles. */}
        <fieldset
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            padding: 12,
            margin: 0,
          }}
        >
          <legend style={{ fontSize: 13, fontWeight: 500, padding: '0 6px' }}>
            {m.ui_kakie_spravochniki_sinhronizirovat()}
          </legend>
          <p className={styles.muted} style={{ marginTop: 0, fontSize: 12 }}>
            {m.ui_snyatyy_chekboks_isklyuchaet_kategoriyu_iz_master_data_sy()}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {(
              [
                ['items', m.admin_integrations_scope_items()],
                ['warehouses', m.admin_integrations_scope_warehouses()],
                ['boms', m.admin_integrations_scope_boms()],
                ['counterparties', m.admin_integrations_scope_counterparties()],
                ['batches', m.admin_integrations_scope_batches()],
              ] as const
            ).map(([key, label]) => (
              <label
                key={key}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
              >
                <input
                  type="checkbox"
                  checked={scope[key]}
                  onChange={(e) => setScope((s) => ({ ...s, [key]: e.target.checked }))}
                />
                {label}
              </label>
            ))}
          </div>
          {noneSelected && (
            <p
              style={{
                margin: '8px 0 0 0',
                fontSize: 12,
                color: 'var(--color-warn, var(--text-secondary))',
              }}
            >
              {m.ui_vse_kategorii_otklyucheny_integraciya_ne_budet_sinhronizi()}
            </p>
          )}
        </fieldset>
      </div>

      <h4 className={styles.editSubheader}>{m.ui_uchetnye_dannye()}</h4>
      <p className={styles.muted}>
        Текущие значения замаскированы из соображений безопасности. Введите новое значение для
        ротации. Пустое поле = оставить текущее.
      </p>
      {credFields.map((f) => {
        const existing = integration.credentials?.find((c) => c.key === f.key);
        return (
          <div key={f.key} className={styles.formRow}>
            <Input
              label={`${f.label}${f.required ? ' *' : ''}`}
              type={f.type === 'password' ? 'password' : 'text'}
              value={creds[f.key] ?? ''}
              onChange={(e) => setCreds((s) => ({ ...s, [f.key]: e.target.value }))}
              placeholder={
                existing?.masked_value
                  ? maskedDisplay(existing.masked_value)
                  : m.admin_integrations_not_set()
              }
              autoComplete="off"
            />
          </div>
        );
      })}
    </Dialog>
  );
}
